import type { MisconceptionDefinition } from '../types';

/**
 * TAA — Woordsoorten sub-registry.
 *
 * Misconcepties rond het herkennen en classificeren van woordsoorten:
 * lidwoord (LID), bijwoord (BW), voornaamwoord (VNW), telwoord (TW),
 * voegwoord (VW) en voorzetsel (VZ). Families: `TAA-SOORT-*`.
 *
 * Onderdeel van de TAA-barrel-splitsing (juni 2026).
 */
export const TAA_SOORTEN_MISCONCEPTIONS: MisconceptionDefinition[] = [
// ─── Lidwoord / zelfstandig naamwoord (TAA-SOORT-LID) ──────────────────
{
  id: 'taa.verb-as-noun',
  label: 'Verwart werkwoord met zelfstandig naamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een werkwoord een zelfstandig naamwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.preposition-as-noun',
  label: 'Verwart voorzetsel met zelfstandig naamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een voorzetsel een zelfstandig naamwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.article-as-noun',
  label: 'Verwart lidwoord met zelfstandig naamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een lidwoord een zelfstandig naamwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.de-het-confused',
  label: 'Verwart de-woord met het-woord',
  module: 'Taalbegrip',
  description: 'Leerling kent het verschil tussen de-woorden en het-woorden niet.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.article-only-een',
  label: 'Denkt dat alleen een onbepaald lidwoord is',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat alleen "een" een lidwoord is en vergeet de/het.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.no-article-needed',
  label: 'Denkt dat woorden geen lidwoord hebben',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat zelfstandige naamwoorden geen lidwoord nodig hebben.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.noun-undercount',
  label: 'Telt te weinig zelfstandige naamwoorden',
  module: 'Taalbegrip',
  description: 'Leerling mist zelfstandige naamwoorden bij het tellen.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.noun-overcount',
  label: 'Telt niet-naamwoorden mee als zelfstandig naamwoord',
  module: 'Taalbegrip',
  description: 'Leerling telt werkwoorden of voorzetsels mee als zelfstandige naamwoorden.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.noun-as-article',
  label: 'Verwart zelfstandig naamwoord met lidwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een zelfstandig naamwoord een lidwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.verb-as-article',
  label: 'Verwart werkwoord met lidwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een werkwoord een lidwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.agent-noun-as-verb',
  label: 'Verwart persoonsnaam (-er) met werkwoord',
  module: 'Taalbegrip',
  description: 'Leerling verwart een afgeleid naamwoord (fietser) met het werkwoord (fietsen).',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.noun-as-verb',
  label: 'Verwart zelfstandig naamwoord met werkwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een zelfstandig naamwoord een werkwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
}, {
  id: 'taa.preposition-as-verb',
  label: 'Verwart voorzetsel met werkwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een voorzetsel een werkwoord is.',
  typicalFamilies: ['TAA-SOORT-LID']
},
// ─── Bijwoord (TAA-SOORT-BW) ────────────────────────────────────────────
{
  id: 'taa.noun-as-adverb',
  label: 'Verwart zelfstandig naamwoord met bijwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een zelfstandig naamwoord een bijwoord is.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.verb-as-adverb',
  label: 'Verwart werkwoord met bijwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een werkwoord een bijwoord is.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-position-rule',
  label: 'Denkt dat positie in zin het verschil BNW/bijwoord bepaalt',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat de positie in de zin bepaalt of iets een BNW of bijwoord is.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-bnw-same',
  label: 'Denkt dat bijwoord en BNW dezelfde woordsoort zijn',
  module: 'Taalbegrip',
  description: 'Leerling ziet geen verschil tussen bijwoord en bijvoeglijk naamwoord.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-always-first',
  label: 'Denkt dat bijwoord altijd aan begin van zin staat',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een bijwoord altijd aan het begin van de zin staat.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.bnw-as-adverb',
  label: 'Verwart BNW bij znw met bijwoord',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat een woord bij een zelfstandig naamwoord een BNW is, niet een bijwoord.',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-place-as-time',
  label: 'Verwart bijwoord van plaats met tijd',
  module: 'Taalbegrip',
  description: 'Leerling verwart een bijwoord van plaats (waar?) met een bijwoord van tijd (wanneer?).',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-manner-as-time',
  label: 'Ver wart bijwoord van wijze met tijd',
  module: 'Taalbegrip',
  description: 'Leerling verwart een bijwoord van wijze (hoe?) met een bijwoord van tijd (wanneer?).',
  typicalFamilies: ['TAA-SOORT-BW']
}, {
  id: 'taa.adverb-degree-as-time',
  label: 'Verwart bijwoord van mate met tijd',
  module: 'Taalbegrip',
  description: 'Leerling verwart een bijwoord van mate met een bijwoord van tijd.',
  typicalFamilies: ['TAA-SOORT-BW']
},
// ─── Voornaamwoord (TAA-SOORT-VNW) ──────────────────────────────────────
{
  id: 'taa.znw-as-vnw',
  label: 'Verwart zelfstandig naamwoord met voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een zelfstandig naamwoord een voornaamwoord is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.ww-as-vnw',
  label: 'Verwart werkwoord met voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een werkwoord een voornaamwoord is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.vz-as-vnw',
  label: 'Verwart voorzetsel met voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een voorzetsel een voornaamwoord is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.miss-zijn-bezittelijk',
  label: 'Herkent zijn niet als bezittelijk voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling herkent "zijn" niet als bezittelijk voornaamwoord.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.haar-always-bezittelijk',
  label: 'Denkt dat haar altijd bezittelijk is',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "haar" altijd een bezittelijk voornaamwoord is, ook als het persoonlijk is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.no-bezittelijk-recognized',
  label: 'Herkent geen enkel bezittelijk voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling herkent geen enkel bezittelijk voornaamwoord in een zin.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.betrekkelijk-as-aanwijzend',
  label: 'Verwart betrekkelijk met aanwijzend voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling verwart een betrekkelijk voornaamwoord (die/dat na znw) met een aanwijzend voornaamwoord.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.dat-as-aanwijzend',
  label: 'Verwart dat (betrekkelijk) met aanwijzend',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat "dat" in een bijzin aanwijzend is in plaats van betrekkelijk.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.betrekkelijk-after-znw',
  label: 'Herkent niet dat die na een znw betrekkelijk is',
  module: 'Taalbegrip',
  description: 'Leerling herkent niet dat "die" na een zelfstandig naamwoord betrekkelijk is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.hulpww-as-vnw',
  label: 'Verwart hulpwerkwoord met voornaamwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een hulpwerkwoord een voornaamwoord is.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.wrong-vnw-type',
  label: 'Kent juiste voornaamwoorden maar verkeerd type toe',
  module: 'Taalbegrip',
  description: 'Leerling herkent voornaamwoorden maar wijst het verkeerde type toe (persoonlijk vs bezittelijk etc.).',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.miss-het-as-vnw',
  label: 'Vergeet het als persoonlijk voornaamwoord te tellen',
  module: 'Taalbegrip',
  description: 'Leerling vergeet dat "het" ook een persoonlijk voornaamwoord kan zijn.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.miss-multiple-vnw',
  label: 'Telt meerdere voornaamwoorden niet mee',
  module: 'Taalbegrip',
  description: 'Leerling mist meerdere voornaamwoorden bij het tellen.',
  typicalFamilies: ['TAA-SOORT-VNW']
}, {
  id: 'taa.overcount-vnw',
  label: 'Telt niet-voornaamwoorden mee',
  module: 'Taalbegrip',
  description: 'Leerling telt voegwoorden, bijwoorden of werkwoorden mee als voornaamwoorden.',
  typicalFamilies: ['TAA-SOORT-VNW']
},
// ─── Telwoord (TAA-SOORT-TW) ────────────────────────────────────────────
{
  id: 'taa.noun-as-numeral',
  label: 'Verwart zelfstandig naamwoord met telwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een zelfstandig naamwoord in plaats van het telwoord.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.verb-as-numeral',
  label: 'Verwart werkwoord met telwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een werkwoord in plaats van het telwoord.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.cardinal-as-ordinal',
  label: 'Verwart hoofdtelwoord met rangtelwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een hoofdtelwoord wanneer een rangtelwoord wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.compound-as-numeral',
  label: 'Verwart samenstelling met telwoord',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat samenstellingen met getallen (drietal, driedubbel) telwoorden zijn.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.miss-ordinal-as-numeral',
  label: 'Vergeet rangtelwoorden als telwoord te tellen',
  module: 'Taalbegrip',
  description: 'Leerling telt alleen hoofdtelwoorden en vergeet dat rangtelwoorden ook telwoorden zijn.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.miss-cardinal-as-numeral',
  label: 'Vergeet hoofdtelwoorden als telwoord te tellen',
  module: 'Taalbegrip',
  description: 'Leerling telt alleen rangtelwoorden en vergeet hoofdtelwoorden.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.overcount-numerals',
  label: 'Telt te veel telwoorden',
  module: 'Taalbegrip',
  description: 'Leerling telt woorden mee die geen telwoorden zijn.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.cardinal-as-numeral-only',
  label: 'Denkt dat alleen exacte getallen telwoorden zijn',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat onbepaalde hoeveelheden, ook telwoorden zijn, of dat alleen hoofdtelwoorden tellen.',
  typicalFamilies: ['TAA-SOORT-TW']
}, {
  id: 'taa.ordinal-as-numeral-only',
  label: 'Denkt dat alleen rangtelwoorden telwoorden zijn',
  module: 'Taalbegrip',
  description: 'Leerling vergeet dat hoofdtelwoorden ook telwoorden zijn.',
  typicalFamilies: ['TAA-SOORT-TW']
},
// ─── Voegwoord (TAA-SOORT-VW) ───────────────────────────────────────────
{
  id: 'taa.preposition-as-conjunction',
  label: 'Verwart voorzetsel met voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een voorzetsel wanneer een voegwoord wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.article-as-conjunction',
  label: 'Verwart lidwoord met voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een lidwoord wanneer een voegwoord wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.adjective-as-conjunction',
  label: 'Verwart bijvoeglijk naamwoord met voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest een bijvoeglijk naamwoord wanneer een voegwoord wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.contrast-as-reason',
  label: 'Verwart tegenstelling-voegwoord met reden-voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest maar/hoewel wanneer omdat/want wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.choice-as-reason',
  label: 'Verwart keuze-voegwoord met reden-voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest of wanneer omdat wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.addition-as-reason',
  label: 'Verwart toevoeging-voegwoord met reden-voegwoord',
  module: 'Taalbegrip',
  description: 'Leerling kiest en wanneer omdat wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.miss-conjunction-en',
  label: "Vergeet 'en' als voegwoord te tellen",
  module: 'Taalbegrip',
  description: "Leerling vergeet dat 'en' een voegwoord is bij het tellen.",
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.no-conjunctions-seen',
  label: 'Herkent geen voegwoorden in de zin',
  module: 'Taalbegrip',
  description: 'Leerling ziet geen enkel voegwoord in een zin die er meerdere bevat.',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.subordinating-not-conjunction',
  label: 'Denkt dat onderschikkend voegwoord geen voegwoord is',
  module: 'Taalbegrip',
  description: 'Leerling herkent onderschikkende voegwoorden niet als voegwoorden, zoals "omdat", "dat" en "als".',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.coordinating-not-conjunction',
  label: 'Denkt dat nevenschikkend voegwoord geen voegwoord is',
  module: 'Taalbegrip',
  description: 'Leerling herkent nevenschikkende voegwoorden niet als voegwoorden, zoals "maar", "en" en "want".',
  typicalFamilies: ['TAA-SOORT-VW']
}, {
  id: 'taa.time-conjunction-not-conjunction',
  label: 'Denkt dat tijdsvoegwoord geen voegwoord is',
  module: 'Taalbegrip',
  description: 'Leerling herkent tijdsvoegwoorden (terwijl, toen) niet als voegwoorden.',
  typicalFamilies: ['TAA-SOORT-VW']
},
// ─── Voorzetsel (TAA-SOORT-VZ) ──────────────────────────────────────────
{
  id: 'taa.verb-as-preposition',
  label: 'Verwart werkwoord met voorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling kiest een werkwoord wanneer een voorzetsel wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.article-as-preposition',
  label: 'Verwart lidwoord met voorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling kiest een lidwoord wanneer een voorzetsel wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.noun-as-preposition',
  label: 'Verwart zelfstandig naamwoord met voorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling kiest een zelfstandig naamwoord wanneer een voorzetsel wordt gevraagd.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.miss-preposition-count',
  label: 'Telt niet alle voorzetsels',
  module: 'Taalbegrip',
  description: 'Leerling mist een of meer voorzetsels bij het tellen.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.overcount-prepositions',
  label: 'Telt niet-voorzetsels mee als voorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling telt lidwoorden of bijwoorden mee als voorzetsels.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.preposition-as-non-preposition',
  label: 'Denkt dat een voorzetsel geen voorzetsel is',
  module: 'Taalbegrip',
  description: 'Leerling herkent een voorzetsel niet als zodanig.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.preposition-place-as-time',
  label: 'Verwart plaatsvoorzetsel met tijdsvoorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een plaatsvoorzetsel een tijdsrelatie aangeeft.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.preposition-direction-as-time',
  label: 'Verwart richtingsvoorzetsel met tijdsvoorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een richtingsvoorzetsel een tijdsrelatie aangeeft.',
  typicalFamilies: ['TAA-SOORT-VZ']
}, {
  id: 'taa.preposition-manner-as-time',
  label: 'Verwart maniervoorzetsel met tijdsvoorzetsel',
  module: 'Taalbegrip',
  description: 'Leerling denkt dat een maniervoorzetsel een tijdsrelatie aangeeft.',
  typicalFamilies: ['TAA-SOORT-VZ']
}];